import React, { useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import Immutable from 'immutable';
import NewPanelModal from 'components/Dashboards/components/NewPanelModal';
import type { EmbeddedExtensionPanelPayload } from '@aha-app/aha-editor-v2';

interface NewBookmarkPayload {
  bookmarkId: RecordId;
  name: string;
  type?: string;
}

interface NewExampleReportPayload {
  exampleReport: string;
  name: string;
  type: string;
}

export type NewEmbeddedReportPayload =
  | NewBookmarkPayload
  | NewExampleReportPayload
  | EmbeddedExtensionPanelPayload;

interface AddReportModalComponentProps {
  onReportSelected: (payload: NewEmbeddedReportPayload) => void;
  onClose: () => void;
}

const AddReportModal: React.FC<AddReportModalComponentProps> = ({
  onReportSelected,
  onClose,
}) => {
  const addViewCallback = useCallback(
    (payload: any) => {
      onReportSelected({
        type: payload.record_type,
        bookmarkId: payload.id,
        name: payload.name,
        useOriginal: payload.useOriginal,
      });
    },
    [onReportSelected]
  );

  const addExampleViewCallback = useCallback(
    (payload: any) => {
      onReportSelected({
        type: payload.record_type,
        exampleReport: payload.id,
        name: payload.name,
      });
    },
    [onReportSelected]
  );

  const addExtensionPanelCallback = useCallback(
    (payload: any) => {
      onReportSelected({
        type: 'ExtensionPanel',
        extensionIdentifier: payload.extensionIdentifier,
        exportName: payload.exportName,
        name: payload.name,
        bookmarkId: undefined,
      });
    },
    [onReportSelected]
  );

  return (
    <NewPanelModal
      open
      maxPanels={100}
      panelCount={1}
      closePanelModal={onClose}
      addPanelFromExistingReport={addViewCallback}
      addPanelFromExampleReport={addExampleViewCallback}
      addExtensionPanel={addExtensionPanelCallback}
      context='note'
      showExtensions={window.currentAccount?.teamworkOrDevelopFlavor}
      examples={Immutable.fromJS([
        {
          name: 'Goal status',
          description: 'Monitor key goals with status and success metrics',
          path: 'list_goal_status',
          example_type: ['list', 'goals'],
          type: 'list',
        },
        {
          name: 'Features by status',
          description: 'Analyze a summary of work in progress',
          path: 'chart_features_by_status',
          example_type: ['chart', 'features'],
          type: 'chart',
        },
        {
          name: 'Recently shipped features',
          description: 'Monitor monthly trends in feature delivery',
          path: 'chart_recently_shipped_features',
          example_type: ['chart', 'features'],
          type: 'chart',
        },
        {
          name: 'Trending ideas',
          description: 'Identify popular ideas based on recent votes',
          path: 'pivot_trending_ideas',
          example_type: ['pivot', 'ideas'],
          type: 'pivot',
        },
        {
          name: 'Initiative progress',
          description:
            'Track percent of work completed on your strategic initiatives',
          path: 'initiative_gantt',
          example_type: ['roadmap', 'initiatives'],
          type: 'initiative_gantt',
        },
        {
          name: 'Release progress',
          description: 'Visualize the status of upcoming releases',
          path: 'gantt',
          example_type: ['roadmap', 'releases'],
          type: 'gantt',
        },
      ])}
    />
  );
};

export async function chooseReport(): Promise<NewEmbeddedReportPayload | null> {
  const modalWindowEl = document.getElementById('modal-window');
  if (!modalWindowEl) throw new Error('No modal window element found');

  const root = createRoot(modalWindowEl);

  return new Promise<NewEmbeddedReportPayload | null>((resolve, reject) => {
    root.render(
      <AddReportModal
        onReportSelected={payload => {
          root.unmount();
          resolve(payload);
        }}
        onClose={() => {
          root.unmount();
          resolve(null);
        }}
      />
    );
  });
}

export default AddReportModal;
